import apiClient from '@/lib/apiClient';

export interface User {
  id: string;
  username: string;
  first_name: string | null;
  last_name: string | null;
  avatar_id: number;
  profile_image_url: string | null;
  created_at: string;
  updated_at?: string;
}

export interface UserWithInitials extends User {
  initials: string;
  displayName: string;
}

class UserService {
  private cachedUsers: UserWithInitials[] | null = null;

  async getWorkspaceUsers(workspaceId: string): Promise<UserWithInitials[]> {
    try {
      // Check cache first
      if (this.cachedUsers) {
        return this.cachedUsers;
      }

      const users = await apiClient.get<User[]>(`/workspaces/${workspaceId}/users`);

      // Process users to add initials and display names
      const processedUsers = (users || []).map(user => {
        const firstName = user.first_name || '';
        const lastName = user.last_name || '';

        let initials = '';
        let displayName = '';

        if (firstName && lastName) {
          initials = `${firstName[0]}${lastName[0]}`.toUpperCase();
          displayName = `${firstName} ${lastName}`;
        } else if (firstName) {
          initials = firstName.slice(0, 2).toUpperCase();
          displayName = firstName;
        } else if (lastName) {
          initials = lastName.slice(0, 2).toUpperCase();
          displayName = lastName;
        } else {
          // Fall back to username
          const emailParts = user.username.split('@')[0];
          const nameParts = emailParts.split('.');
          if (nameParts.length > 1) {
            initials = `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
            displayName = nameParts.map(p =>
              p.charAt(0).toUpperCase() + p.slice(1)
            ).join(' ');
          } else {
            initials = emailParts.slice(0, 2).toUpperCase();
            displayName = emailParts.charAt(0).toUpperCase() + emailParts.slice(1);
          }
        }

        return {
          ...user,
          initials,
          displayName
        };
      });

      // Cache the processed users
      this.cachedUsers = processedUsers;

      return processedUsers;
    } catch (error) {
      console.error('Error fetching workspace users:', error);
      return [];
    }
  }

  async getUserById(userId: string): Promise<UserWithInitials | null> {
    try {
      const user = await apiClient.get<User>(`/users/${userId}`);
      if (!user) return null;

      const firstName = user.first_name || '';
      const lastName = user.last_name || '';

      let initials = '';
      let displayName = '';

      if (firstName && lastName) {
        initials = `${firstName[0]}${lastName[0]}`.toUpperCase();
        displayName = `${firstName} ${lastName}`;
      } else if (firstName) {
        initials = firstName.slice(0, 2).toUpperCase();
        displayName = firstName;
      } else if (lastName) {
        initials = lastName.slice(0, 2).toUpperCase();
        displayName = lastName;
      } else {
        const emailParts = user.username.split('@')[0];
        initials = emailParts.slice(0, 2).toUpperCase();
        displayName = emailParts;
      }

      return {
        ...user,
        initials,
        displayName
      };
    } catch (error) {
      console.error('Error fetching user:', error);
      return null;
    }
  }

  async updateUser(userId: string, updates: Partial<Pick<User, 'first_name' | 'last_name' | 'avatar_id'>>): Promise<UserWithInitials | null> {
    try {
      const updatedUser = await apiClient.put<User>(`/users/${userId}`, updates);
      if (!updatedUser) return null;

      // Clear cache since user data has changed
      this.clearCache();

      // Process the updated user to add initials and display name
      const firstName = updatedUser.first_name || '';
      const lastName = updatedUser.last_name || '';

      let initials = '';
      let displayName = '';

      if (firstName && lastName) {
        initials = `${firstName[0]}${lastName[0]}`.toUpperCase();
        displayName = `${firstName} ${lastName}`;
      } else if (firstName) {
        initials = firstName.slice(0, 2).toUpperCase();
        displayName = firstName;
      } else if (lastName) {
        initials = lastName.slice(0, 2).toUpperCase();
        displayName = lastName;
      } else {
        const emailParts = updatedUser.username.split('@')[0];
        initials = emailParts.slice(0, 2).toUpperCase();
        displayName = emailParts;
      }

      return {
        ...updatedUser,
        initials,
        displayName
      };
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }

  async uploadProfileImage(userId: string, file: File): Promise<string | null> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.uploadFile<{ profile_image_url: string }>(
        `/users/${userId}/profile-image`,
        formData
      );

      // Clear cache since user data has changed
      this.clearCache();

      return response.profile_image_url;
    } catch (error) {
      console.error('Error uploading profile image:', error);
      throw error;
    }
  }

  async deleteProfileImage(userId: string): Promise<void> {
    try {
      await apiClient.delete(`/users/${userId}/profile-image`);

      // Clear cache since user data has changed
      this.clearCache();
    } catch (error) {
      console.error('Error deleting profile image:', error);
      throw error;
    }
  }

  clearCache() {
    this.cachedUsers = null;
  }
}

export const userService = new UserService();
